import React, { ReactElement, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Nullable, Row, theme } from "uikit";
import { SortType } from "rsuite-table";

import { Parking } from "../../../../../services";
import { useTypedSelector } from "../../../../../redux/store";
import useParkingActions from "../../hooks/useParkingActions";
import { sortByNumberOrString } from "../../../../../utils/sortByNumberOrString";
import CompactTable from "../../../../CompactTable";
import { ExecutorInfo } from "../../../../common";
import { useFilteredExecutors } from "../../../hook";
import { ModelTable } from "../../../Executors";

const StyledCell = styled(Row)<{ active: boolean; background?: string }>`
	padding: 9px 6px;
	color: ${(props) => (props.active ? props.color : "lightgray")} !important;
	background: ${(props) => props?.background || "transparent"};
	width: 100%;
	display: flex;
	gap: 5px;
	font-size: 12px;
	white-space: wrap;
	align-items: center;
	z-index: 1000;

	${theme.longWord}
`;

const StyledCellExecutor = styled(Row)<{
	active: boolean;
	background?: string;
}>`
	padding: 3px 6px;
	color: ${(props) => (props.active ? props.color : "lightgray")} !important;
	background: ${(props) => props?.background || "transparent"};
	width: 100%;
	display: flex;
	gap: 3px;
	font-size: 12px;
	white-space: wrap;
	align-items: flex-start;
	z-index: 1000;

	${theme.longWord}
`;

const StyledExecutorShortName = styled("div")<{
	useBgColor: boolean;
	useTextColor: boolean;
	bgColor: string;
	textColor: string;
}>`
	padding: 2px;
	height: 20px;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	font-size: 11px;
	color: ${(props) =>
		props.useTextColor ? props.textColor : theme.colors.primary};
	background-color: ${(props) =>
		props.useBgColor ? props.bgColor : "transparent"};
	border-radius: 2px;
`;

const ShortName = ({ data }) => {
	const actualExecutorJournal = data.executorShiftJournals?.find(
		(journal) => journal.isLatest,
	)?.car?.class;

	const textColor = actualExecutorJournal?.textColor;
	const backgroundColor = actualExecutorJournal?.backgroundColor;

	const textColorHex = `${textColor?.model}(${textColor?.color?.join(",")})`;
	const backgroundColorHex = `${
		backgroundColor?.model
	}(${backgroundColor?.color?.join(",")})`;

	const carClassData = {
		shortName: actualExecutorJournal?.shortName || "",
		backgroundColor: backgroundColorHex,
		textColor: textColorHex,
		useBackgroundColor: actualExecutorJournal?.useBackgroundColor,
		useTextColor: actualExecutorJournal?.useTextColor,
	};

	return carClassData.shortName.length ? (
		<StyledExecutorShortName
			bgColor={carClassData.backgroundColor}
			textColor={carClassData.textColor}
			useBgColor={carClassData.useBackgroundColor}
			useTextColor={carClassData.useTextColor}
		>
			{carClassData.shortName}
		</StyledExecutorShortName>
	) : (
		<div style={{ height: 0, width: 0, position: "absolute" }}></div>
	);
};

const ParkingTable: React.FC<ParkingTable.Props> = ({
	// executorRef,
	data,
	selectedId,
	onChangeSelectedId,
	onChangeIsEditModal,
	columnIds,
	// sort,
	// onChangeSort,
	onShowParkingModal,
}) => {
	const lang = useTypedSelector((state) => state.session.language);
	const { sort } = useTypedSelector((state) => state.orders.parking.filter);
	const executors = useTypedSelector(
		(state) => state.orders.executors.data.executors,
	);

	const { criteria: filters } = useTypedSelector(
		(state) => state.orders.parking.filter,
	);

	const filteredExecutors = useFilteredExecutors(
		executors,
		{
			companyIds: filters.companyIds,
			taxiServiceIds: filters.taxiServiceIds,
			executorGroupIds: filters.executorGroupIds,
		},
		{
			companyIds: true,
			taxiServiceIds: true,
			executorGroupIds: true,
		},
	);

	const { setParkingsSort } = useParkingActions();
	const { t } = useTranslation();
	// const sortColumn = useCallback(
	// 	(column, type, taxiServiceId) => {
	// 		onChangeSort({ column, type }, taxiServiceId);
	// 	},
	// 	[onChangeSort],
	// );

	const tableRowClassName = useCallback(
		(rowData) => {
			if (rowData?.id === selectedId) return "founded";

			if (!rowData?.active) return "not-active";

			return "";
		},
		[selectedId],
	);

	const onRowSelect = useCallback(
		(rowData) => {
			if (rowData.id === selectedId) {
				onChangeSelectedId(0);
			} else {
				onChangeSelectedId(rowData.id);
			}
		},
		[onChangeSelectedId, selectedId],
	);

	const onRowDoubleClick = useCallback(
		(rowData) => {
			onChangeSelectedId(rowData.id);
			onChangeIsEditModal(true);
			onShowParkingModal(true);
		},
		[onChangeIsEditModal, onChangeSelectedId, onShowParkingModal],
	);

	const getExecutorById = useCallback(
		(id: number) => {
			const exist = filteredExecutors.find((item) => item.id === id);
			return exist;
		},
		[filteredExecutors],
	);

	const Columns: Record<string, () => ReactElement> = {
		name: () => (
			<CompactTable.Column flexGrow={0.2} sortable>
				<CompactTable.HeaderCell verticalAlign="middle">
					{t("orderPageWidgets.parking.parkingTable.str150") ?? ""}
				</CompactTable.HeaderCell>
				<CompactTable.Cell
					align="center"
					dataKey="name"
					wordWrap
					fullText
					style={{ cursor: "pointer" }}
				>
					{(rowData) => (
						<StyledCell
							active={rowData.active}
							justify="flex-start"
							style={{ fontSize: 14 }}
						>
							{rowData.name?.[lang] ?? ""}
						</StyledCell>
					)}
				</CompactTable.Cell>
			</CompactTable.Column>
		),
		driversAmount: () => (
			<CompactTable.Column width={44} sortable>
				<CompactTable.HeaderCell verticalAlign="top">
					{t("orderPageWidgets.parking.parkingTable.str151") ?? ""}
				</CompactTable.HeaderCell>
				<CompactTable.Cell
					align="center"
					dataKey="executorsCount"
					fullText
					style={{ fontSize: 14, cursor: "pointer" }}
				>
					{(rowData) => {
						const numberLength: number = rowData.executorsQueue
							?.length
							? (
									rowData.executorsQueue?.filter((queue) =>
										getExecutorById(
											queue.executor?.id || -3,
										),
									) || []
							  ).length
							: 0;

						return (
							<StyledCell
								active={rowData.active}
								justify="center"
							>
								{numberLength}
							</StyledCell>
						);
					}}
				</CompactTable.Cell>
			</CompactTable.Column>
		),
		aliases: () => (
			<CompactTable.Column flexGrow={0.4} sortable>
				<CompactTable.HeaderCell
					verticalAlign="middle"
					itemID="executorsColumn"
				>
					{t("orderPageWidgets.parking.parkingTable.str152") ?? ""}
				</CompactTable.HeaderCell>
				<CompactTable.Cell
					dataKey="executors"
					wordWrap
					fullText
					autoHeight
					style={{ cursor: "pointer" }}
				>
					{(rowData) => (
						<StyledCellExecutor active={rowData.active} wrap="wrap">
							{rowData.executorsQueue?.length ? (
								rowData.executorsQueue.map((queue, index) => (
									<Row
										key={queue?.executor?.id || index}
										align="center"
										gaps="2px"
									>
										<ExecutorInfo
											widgetType="Parking"
											id={queue?.executor?.id}
											speed={
												queue?.executor?.location?.speed
											}
										/>
										<ShortName data={queue.executor} />
									</Row>
								))
							) : (
								<div style={{ height: 26 }}></div>
							)}
						</StyledCellExecutor>
					)}
				</CompactTable.Cell>
			</CompactTable.Column>
		),
	};

	const valueSort = useMemo(() => {
		if (!data?.data?.length) return [];
		const value = data.data;
		// const key = `${sort.column}_${sort.type}_${lang}`;

		if (sort.type === "asc") {
			if (sort.column === "name") {
				const items = [...value].sort((prev, next) => {
					const prevName = prev.name?.[lang];
					const nextName = next.name?.[lang];
					return sortByNumberOrString(prevName, nextName);
				});

				return items;
			}
			if (sort.column === "driversAmount") {
				const items = [...value].sort((prev, next) => {
					const prevName = prev?.executorsQueue?.length || 0;
					const nextName = next?.executorsQueue?.length || 0;
					return sortByNumberOrString(prevName, nextName);
				});

				return items;
			}

			if (sort.column === "aliases") {
				const items = [...value].sort((prev, next) => {
					const prevName = prev.executorsQueue
						.map((queue) => `${queue.executor.id}`)
						.join(",");
					const nextName = next.executorsQueue
						.map((queue) => `${queue.executor.id}`)
						.join(",");
					return sortByNumberOrString(prevName, nextName);
				});

				return items;
			}
		}

		// desc
		if (sort.type === "desc") {
			if (sort.column === "name") {
				const items = [...value].sort((prev, next) => {
					const prevName = next.name?.[lang];
					const nextName = prev.name?.[lang];
					return sortByNumberOrString(prevName, nextName);
				});

				return items;
			}
			if (sort.column === "driversAmount") {
				const items = [...value].sort((prev, next) => {
					const prevName = next?.executorsQueue?.length || 0;
					const nextName = prev?.executorsQueue?.length || 0;
					return sortByNumberOrString(prevName, nextName);
				});

				return items;
			}

			if (sort.column === "aliases") {
				const items = [...value].sort((prev, next) => {
					const prevName = next.executorsQueue
						.map((queue) => `${queue.executor.id}`)
						.join(",");
					const nextName = prev.executorsQueue
						.map((queue) => `${queue.executor.id}`)
						.join(",");
					return sortByNumberOrString(prevName, nextName);
				});

				return items;
			}
		}

		return value;
	}, [data?.data, lang, sort.column, sort.type]);

	return (
		<CompactTable
			// ref={executorRef}
			virtualized
			data={valueSort}
			headerHeight={26}
			sortColumn={sort.column}
			sortType={sort.type}
			onSortColumn={
				(column, type) => setParkingsSort({ column, type })
				// sortColumn(column, type, data.data[0].taxiServiceId)
			}
			autoHeight
			loading={false}
			shouldUpdateScroll={false}
			rowClassName={tableRowClassName}
			onRowClick={onRowSelect}
			onRowDoubleClick={onRowDoubleClick}
		>
			{columnIds.map((columnId) => Columns[columnId]())}
		</CompactTable>
	);
};
declare namespace ParkingTable {
	interface Props {
		// executorRef: React.MutableRefObject<any>;
		data: { type?: SortType; column?: SortType; data: Parking.Model[] };
		selectedId: Nullable<number>;
		onChangeSelectedId: React.Dispatch<React.SetStateAction<number>>;
		onChangeIsEditModal: React.Dispatch<React.SetStateAction<boolean>>;
		columnIds: string[];
		sort: ModelTable.Sort;
		onChangeSort: (
			{
				type,
				column,
			}: {
				type: SortType;
				column: SortType;
			},
			taxiServiceId: number,
		) => void;
		onShowParkingModal: React.Dispatch<React.SetStateAction<boolean>>;
	}
}

export default ParkingTable;
